<template>
  <div class="periodic-wrapper">
    <div class="topbar">
      <img src="@/assets/logogruas.png" alt="HomePage" class="logo-button" @click="goBack" />
      <div class="topbar-actions">
        <button class="config-btn" @click="openConfig">Configuracion</button>
        <button class="refresh-btn" @click="fetchData" :disabled="loading">
          {{ loading ? 'Actualizando...' : 'Actualizar' }}
        </button>
      </div>
    </div>

    <section class="header">
      <h1>Mantenimiento Periodico de Unidades</h1>
      <p>Seguimiento de preventivo configurable por unidad (default {{ formatNumber(defaultUnitIntervalKm) }} km)</p>
    </section>

    <section v-if="error" class="error-box">
      {{ error }}
    </section>

    <section v-if="visibleRows.length > 0" class="filters-bar">
      <div class="filter-group grow">
        <label for="periodic-search">Buscar</label>
        <input
          id="periodic-search"
          v-model="filters.search"
          type="search"
          placeholder="Unidad, estado, fecha, kilometraje..."
        />
      </div>
      <div class="filter-group">
        <label>Estado</label>
        <div class="status-chips">
          <button
            v-for="option in statusOptions"
            :key="option.value"
            class="status-chip"
            :class="[statusClassByKey[option.value], { 'is-active': filters.statuses.includes(option.value) }]"
            @click="toggleStatusFilter(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
      <div class="filter-group">
        <label>KM para próximo preventivo</label>
        <div class="range-inputs">
          <input v-model="filters.kmUntilMin" type="number" step="1" placeholder="Min" />
          <span class="range-sep">a</span>
          <input v-model="filters.kmUntilMax" type="number" step="1" placeholder="Max" />
        </div>
      </div>
      <div class="filter-group filters-meta">
        <span class="filters-count">Mostrando {{ displayRows.length }} de {{ visibleRows.length }}</span>
        <button class="small-btn" :disabled="!hasActiveFilters" @click="clearFilters">Limpiar filtros</button>
      </div>
    </section>

    <section v-if="!loading && rows.length === 0" class="empty-box">
      No hay unidades para mostrar.
    </section>
    <section v-else-if="!loading && visibleRows.length === 0" class="empty-box">
      No hay unidades seleccionadas en configuracion.
    </section>
    <section v-else-if="!loading && displayRows.length === 0" class="empty-box">
      Ninguna unidad coincide con los filtros aplicados.
    </section>

    <section v-if="displayRows.length > 0" class="table-wrap">
      <table class="periodic-table">
        <thead>
          <tr>
            <th v-for="column in columns" :key="column.key">
              {{ column.label }}
              <button
                class="sort-btn"
                :class="{ 'is-active': isSorted(column.key) }"
                :title="`Ordenar por ${column.label}`"
                @click="toggleSort(column.key)"
              >
                {{ sortIcon(column.key) }}
              </button>
            </th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in displayRows"
            :key="row.unit_short_id || `unit-row-${idx}`"
            class="table-row"
            :class="statusClass(row)"
          >
            <td>{{ row.unit_short_id || 'NO HAY INFORMACION' }}</td>
            <td>
              <span class="badge">{{ statusLabel(row) }}</span>
              <span
                v-if="!row?.has_information && row?.missing_info_reason"
                class="missing-info-tooltip"
                :title="row.missing_info_reason"
              >
                i
              </span>
            </td>
            <td>{{ formatNumber(row.current_odometer) }}</td>
            <td>{{ formatDate(row.last_maintenance_time) }}</td>
            <td>{{ formatNumber(row.last_maintenance_km) }}</td>
            <td>{{ formatNumber(getRowInterval(row)) }}</td>
            <td>{{ formatKmUntil(row) }}</td>
            <td>
              <button class="schedule-btn" @click="openScheduleModal(row)">Programar</button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <div v-if="showConfigModal" class="modal-overlay" @click.self="closeConfig">
      <div class="modal-card">
        <div class="modal-head">
          <h3>Configuracion de unidades visibles</h3>
          <button class="close-btn" @click="closeConfig">Cerrar</button>
        </div>
        <div class="modal-actions">
          <button class="small-btn" @click="selectAllUnits">Seleccionar todo</button>
          <button class="small-btn" @click="clearAllUnits">Quitar todo</button>
        </div>
        <div class="units-list">
          <div v-for="unit in availableUnits" :key="unit" class="unit-option">
            <input
              type="checkbox"
              :checked="(selectedUnits || []).includes(unit)"
              @change="toggleUnit(unit, $event.target.checked)"
            />
            <span>{{ unit }}</span>
            <input
              class="unit-km-input"
              type="number"
              min="1"
              step="1"
              :value="getUnitInterval(unit)"
              @change="updateUnitInterval(unit, $event.target.value)"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="showScheduleModal" class="modal-overlay" @click.self="closeScheduleModal">
      <div class="modal-card schedule-modal">
        <div class="modal-head">
          <h3>Programar mantenimiento periodico</h3>
          <button class="close-btn" @click="closeScheduleModal">Cerrar</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label>Unidad</label>
            <input type="text" :value="scheduleForm.unit_short_id" disabled />
          </div>
          <div class="form-row">
            <label>Motivo de servicio</label>
            <input type="text" v-model="scheduleForm.motive_of_service" />
          </div>
          <div class="form-row">
            <label>Programada para</label>
            <input type="datetime-local" v-model="scheduleForm.scheduled_for" />
          </div>
          <div class="form-row">
            <label>Descripcion de servicio</label>
            <textarea v-model="scheduleForm.service_description" />
          </div>
        </div>
        <div class="modal-actions footer-actions">
          <button class="small-btn" @click="closeScheduleModal">Cancelar</button>
          <button class="small-btn primary" :disabled="creatingOrder" @click="submitScheduledOrder">
            {{ creatingOrder ? 'Guardando...' : 'Crear orden' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'

export default {
  name: 'UnitPeriodicMaintenance',
  data() {
    return {
      rows: [],
      selectedUnits: null,
      unitIntervalKmByShortId: {},
      defaultUnitIntervalKm: 10000,
      showConfigModal: false,
      showScheduleModal: false,
      loading: false,
      error: '',
      savingConfig: false,
      creatingOrder: false,
      sortState: { by: '', dir: 'asc' },
      filters: {
        search: '',
        statuses: [],
        kmUntilMin: '',
        kmUntilMax: '',
      },
      columns: [
        { key: 'unit_short_id', label: 'Unidad' },
        { key: 'status', label: 'Estado' },
        { key: 'current_odometer', label: 'Odómetro actual' },
        { key: 'last_maintenance_time', label: 'Último preventivo (fecha)' },
        { key: 'last_maintenance_km', label: 'KM último preventivo' },
        { key: 'interval', label: 'Intervalo preventivo (km)' },
        { key: 'km_until_next', label: 'KM para próximo preventivo' },
      ],
      statusOptions: [
        { value: 'overdue', label: 'VENCIDO' },
        { value: 'red', label: 'URGENTE' },
        { value: 'yellow', label: 'PRONTO' },
        { value: 'green', label: 'OK' },
        { value: 'missing', label: 'SIN INFO' },
      ],
      statusClassByKey: {
        overdue: 'is-overdue',
        red: 'is-red',
        yellow: 'is-yellow',
        green: 'is-green',
        missing: 'is-info-missing',
      },
      scheduleForm: {
        unit_short_id: '',
        motive_of_service: 'Preventivo',
        scheduled_for: '',
        service_description: 'ORDEN CREADA AUTOMATICAMENTE',
      },
    }
  },
  computed: {
    availableUnits() {
      const unique = new Set(
        this.rows
          .map((row) => row.unit_short_id)
          .filter((value) => Boolean(value))
      )
      return Array.from(unique).sort((a, b) => String(a).localeCompare(String(b)))
    },
    visibleRows() {
      if (this.selectedUnits === null) return this.rows
      const allowed = new Set(this.selectedUnits)
      return this.rows.filter((row) => allowed.has(row.unit_short_id))
    },
    filteredRows() {
      const term = this.filters.search.trim().toLowerCase()
      const statuses = this.filters.statuses
      const kmUntilMin = this.parseRangeValue(this.filters.kmUntilMin)
      const kmUntilMax = this.parseRangeValue(this.filters.kmUntilMax)
      return this.visibleRows.filter((row) => {
        if (term && !this.rowSearchText(row).includes(term)) return false
        if (statuses.length > 0 && !statuses.includes(this.statusKey(row))) return false
        if (kmUntilMin !== null || kmUntilMax !== null) {
          const kmUntil = this.sortValue(row, 'km_until_next')
          if (kmUntil === null) return false
          if (kmUntilMin !== null && kmUntil < kmUntilMin) return false
          if (kmUntilMax !== null && kmUntil > kmUntilMax) return false
        }
        return true
      })
    },
    displayRows() {
      if (!this.sortState.by) return this.filteredRows
      return [...this.filteredRows].sort((left, right) => this.compareRows(left, right))
    },
    hasActiveFilters() {
      return Boolean(
        this.filters.search.trim() ||
          this.filters.statuses.length > 0 ||
          this.filters.kmUntilMin !== '' ||
          this.filters.kmUntilMax !== ''
      )
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      this.error = ''
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const { data } = await axios.get(`${apiDirectory}/maintenance/periodic-dashboard`, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
        })
        this.rows = Array.isArray(data?.results) ? data.results : []
        await this.fetchConfig()
        this.syncUnitSelection()
      } catch (err) {
        this.error = 'No se pudo cargar el tablero periodico.'
      } finally {
        this.loading = false
      }
    },
    openConfig() {
      this.showConfigModal = true
    },
    closeConfig() {
      this.showConfigModal = false
    },
    openScheduleModal(row) {
      this.scheduleForm = {
        unit_short_id: row?.unit_short_id || '',
        motive_of_service: 'Preventivo',
        scheduled_for: '',
        service_description: 'ORDEN CREADA AUTOMATICAMENTE',
      }
      this.showScheduleModal = true
    },
    closeScheduleModal() {
      this.showScheduleModal = false
    },
    async submitScheduledOrder() {
      if (!this.scheduleForm.unit_short_id) {
        this.error = 'No se puede crear orden sin unidad.'
        return
      }
      if (!this.scheduleForm.scheduled_for) {
        this.error = 'Selecciona una fecha para Programada para.'
        return
      }
      if (!this.scheduleForm.motive_of_service?.trim() || !this.scheduleForm.service_description?.trim()) {
        this.error = 'Completa motivo y descripcion.'
        return
      }
      const apiDirectory = process.env.API_DIRECTORY
      this.creatingOrder = true
      this.error = ''
      try {
        await axios.post(
          `${apiDirectory}/create-maintenance-order`,
          {
            unit_short_id: this.scheduleForm.unit_short_id,
            motive_of_service: this.scheduleForm.motive_of_service.trim(),
            scheduled_for: this.toServerDateTime(this.scheduleForm.scheduled_for),
            service_description: this.scheduleForm.service_description.trim(),
          },
          {
            headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
          }
        )
        this.closeScheduleModal()
        await Swal.fire({
          icon: 'success',
          title: 'Orden creada',
          text: `Se programo una orden para unidad ${this.scheduleForm.unit_short_id}.`,
          confirmButtonText: 'OK',
        })
      } catch (err) {
        this.error = 'No se pudo crear la orden de mantenimiento.'
      } finally {
        this.creatingOrder = false
      }
    },
    toServerDateTime(localValue) {
      if (!localValue) return null
      return new Date(localValue).toISOString()
    },
    toggleUnit(unitShortId, checked) {
      if (this.selectedUnits === null) this.selectedUnits = [...this.availableUnits]
      if (checked) {
        if (!this.selectedUnits.includes(unitShortId)) {
          this.selectedUnits = [...this.selectedUnits, unitShortId]
        }
      } else {
        this.selectedUnits = this.selectedUnits.filter((value) => value !== unitShortId)
      }
      this.saveConfig()
    },
    selectAllUnits() {
      this.selectedUnits = [...this.availableUnits]
      this.saveConfig()
    },
    clearAllUnits() {
      this.selectedUnits = []
      this.saveConfig()
    },
    async fetchConfig() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const { data } = await axios.get(`${apiDirectory}/maintenance/periodic-dashboard-config`, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
        })
        const fromApi = Array.isArray(data?.visible_unit_short_ids) ? data.visible_unit_short_ids : []
        this.unitIntervalKmByShortId = this.normalizeUnitIntervals(data?.unit_interval_km_by_short_id)
        this.defaultUnitIntervalKm = this.normalizeIntervalValue(data?.default_unit_interval_km, 10000)
        this.selectedUnits = data?.is_configured ? fromApi : [...this.availableUnits]
      } catch (err) {
        this.unitIntervalKmByShortId = {}
        this.defaultUnitIntervalKm = 10000
        this.selectedUnits = [...this.availableUnits]
      }
    },
    syncUnitSelection() {
      if (this.selectedUnits === null) {
        this.selectedUnits = [...this.availableUnits]
      } else {
        const availableSet = new Set(this.availableUnits)
        this.selectedUnits = this.selectedUnits.filter((unit) => availableSet.has(unit))
      }
      const availableSet = new Set(this.availableUnits)
      const nextIntervals = {}
      Object.entries(this.unitIntervalKmByShortId || {}).forEach(([unitShortId, intervalKm]) => {
        if (availableSet.has(unitShortId)) {
          nextIntervals[unitShortId] = this.normalizeIntervalValue(intervalKm, this.defaultUnitIntervalKm)
        }
      })
      this.unitIntervalKmByShortId = nextIntervals
    },
    async saveConfig() {
      const apiDirectory = process.env.API_DIRECTORY
      this.savingConfig = true
      try {
        await axios.put(
          `${apiDirectory}/maintenance/periodic-dashboard-config`,
          {
            visible_unit_short_ids: this.selectedUnits || [],
            unit_interval_km_by_short_id: this.unitIntervalKmByShortId || {},
          },
          {
            headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
          }
        )
      } catch (err) {
        this.error = 'No se pudo guardar la configuracion global.'
      } finally {
        this.savingConfig = false
      }
    },
    goBack() {
      this.$router.push({ path: '/modulo-mantenimiento' })
    },
    formatNumber(value) {
      if (value === null || value === undefined) return 'NO HAY INFORMACION'
      return new Intl.NumberFormat('es-MX').format(value)
    },
    formatDate(value) {
      if (!value) return 'NO HAY INFORMACION'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return 'NO HAY INFORMACION'
      return new Intl.DateTimeFormat('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(parsed)
    },
    formatKmUntil(row) {
      if (!row?.has_information || row.km_until_next === null || row.km_until_next === undefined) {
        return 'NO HAY INFORMACION'
      }
      if (row.km_until_next < 0) {
        return `VENCIDO por ${new Intl.NumberFormat('es-MX').format(Math.abs(row.km_until_next))} km`
      }
      return new Intl.NumberFormat('es-MX').format(row.km_until_next)
    },
    getUnitInterval(unitShortId) {
      return this.normalizeIntervalValue(
        this.unitIntervalKmByShortId?.[unitShortId],
        this.defaultUnitIntervalKm
      )
    },
    getRowInterval(row) {
      return this.normalizeIntervalValue(row?.maintenance_interval_km, this.getUnitInterval(row?.unit_short_id))
    },
    updateUnitInterval(unitShortId, rawValue) {
      if (!unitShortId) return
      const parsed = this.normalizeIntervalValue(rawValue, this.defaultUnitIntervalKm)
      this.unitIntervalKmByShortId = {
        ...(this.unitIntervalKmByShortId || {}),
        [unitShortId]: parsed,
      }
      this.saveConfig()
    },
    normalizeIntervalValue(rawValue, fallbackValue) {
      const parsed = Number.parseInt(rawValue, 10)
      if (!Number.isFinite(parsed) || parsed <= 0) return fallbackValue
      return parsed
    },
    normalizeUnitIntervals(rawMap) {
      if (!rawMap || typeof rawMap !== 'object') return {}
      const normalized = {}
      Object.entries(rawMap).forEach(([unitShortId, rawValue]) => {
        const trimmed = String(unitShortId || '').trim()
        if (!trimmed) return
        normalized[trimmed] = this.normalizeIntervalValue(rawValue, this.defaultUnitIntervalKm)
      })
      return normalized
    },
    statusKey(row) {
      if (!row?.has_information) return 'missing'
      if (row.status_level === 'overdue') return 'overdue'
      if (row.status_level === 'red') return 'red'
      if (row.status_level === 'yellow') return 'yellow'
      return 'green'
    },
    statusLabel(row) {
      const key = this.statusKey(row)
      const option = this.statusOptions.find((item) => item.value === key)
      return option ? option.label : 'OK'
    },
    statusClass(row) {
      return this.statusClassByKey[this.statusKey(row)]
    },
    toggleStatusFilter(statusValue) {
      if (this.filters.statuses.includes(statusValue)) {
        this.filters.statuses = this.filters.statuses.filter((value) => value !== statusValue)
      } else {
        this.filters.statuses = [...this.filters.statuses, statusValue]
      }
    },
    clearFilters() {
      this.filters = { search: '', statuses: [], kmUntilMin: '', kmUntilMax: '' }
    },
    parseRangeValue(rawValue) {
      if (rawValue === '' || rawValue === null || rawValue === undefined) return null
      const parsed = Number(rawValue)
      return Number.isFinite(parsed) ? parsed : null
    },
    rowSearchText(row) {
      return [
        row?.unit_short_id,
        this.statusLabel(row),
        this.formatNumber(row?.current_odometer),
        this.formatDate(row?.last_maintenance_time),
        this.formatNumber(row?.last_maintenance_km),
        this.formatNumber(this.getRowInterval(row)),
        this.formatKmUntil(row),
      ]
        .join(' ')
        .toLowerCase()
    },
    isSorted(field) {
      return this.sortState.by === field
    },
    sortIcon(field) {
      if (!this.isSorted(field)) return '↕'
      return this.sortState.dir === 'asc' ? '▲' : '▼'
    },
    toggleSort(field) {
      if (this.sortState.by === field) {
        this.sortState.dir = this.sortState.dir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortState.by = field
        this.sortState.dir = 'asc'
      }
    },
    toSortableNumber(rawValue) {
      if (rawValue === null || rawValue === undefined || rawValue === '') return null
      const parsed = Number(rawValue)
      return Number.isFinite(parsed) ? parsed : null
    },
    sortValue(row, field) {
      if (field === 'unit_short_id') {
        return row?.unit_short_id ? String(row.unit_short_id) : null
      }
      if (field === 'status') return this.statusRank(row)
      if (field === 'interval') return this.getRowInterval(row)
      if (field === 'last_maintenance_time') {
        if (!row?.last_maintenance_time) return null
        const parsed = new Date(row.last_maintenance_time).getTime()
        return Number.isNaN(parsed) ? null : parsed
      }
      if (field === 'km_until_next') {
        if (!row?.has_information) return null
        return this.toSortableNumber(row.km_until_next)
      }
      return this.toSortableNumber(row?.[field])
    },
    // Ranked by urgency instead of label, so ascending order lists the most urgent units first.
    statusRank(row) {
      const ranks = { overdue: 0, red: 1, yellow: 2, green: 3, missing: 4 }
      return ranks[this.statusKey(row)]
    },
    compareRows(leftRow, rightRow) {
      const { by, dir } = this.sortState
      const left = this.sortValue(leftRow, by)
      const right = this.sortValue(rightRow, by)
      // Rows without information stay at the bottom in both directions.
      if (left === null && right === null) return 0
      if (left === null) return 1
      if (right === null) return -1
      const result =
        typeof left === 'string' || typeof right === 'string'
          ? String(left).localeCompare(String(right), 'es-MX', { numeric: true, sensitivity: 'base' })
          : left - right
      return dir === 'desc' ? -result : result
    },
  },
}
</script>

<style scoped>
.periodic-wrapper {
  box-sizing: border-box;
  height: 100vh;
  background: #f6f8fb;
  padding: 76px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
}

.periodic-wrapper > .header,
.periodic-wrapper > .filters-bar,
.periodic-wrapper > .error-box,
.periodic-wrapper > .empty-box {
  flex: 0 0 auto;
}

.topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: #ffc434;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  z-index: 20;
}

.logo-button {
  height: 65%;
  object-fit: contain;
  cursor: pointer;
}

.refresh-btn {
  border: none;
  background: #111827;
  color: #fff;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
}

.topbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.config-btn {
  border: 1px solid #0f172a;
  background: #fff;
  color: #0f172a;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 600;
}

.schedule-btn {
  border: none;
  background: #2563eb;
  color: #fff;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
}

.missing-info-tooltip {
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #0f172a;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: help;
}

.refresh-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.header h1 {
  margin: 0;
  color: #0f172a;
  font-size: 1.25rem;
}

.header p {
  margin: 4px 0 0;
  color: #475569;
}

.filters-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 12px;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.06);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-group.grow {
  flex: 1 1 220px;
  min-width: 200px;
}

.filter-group > label {
  color: #334155;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.filter-group input {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 0.9rem;
}

.status-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.status-chip {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #475569;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
}

.status-chip.is-active {
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.25);
}

.status-chip.is-active.is-green {
  background: #dcfce7;
  color: #166534;
  border-color: #86efac;
}

.status-chip.is-active.is-yellow {
  background: #fef3c7;
  color: #92400e;
  border-color: #fde68a;
}

.status-chip.is-active.is-red {
  background: #fee2e2;
  color: #991b1b;
  border-color: #fecaca;
}

.status-chip.is-active.is-overdue {
  background: #ef4444;
  color: #fff;
  border-color: #dc2626;
}

.status-chip.is-active.is-info-missing {
  background: #e2e8f0;
  color: #334155;
  border-color: #cbd5e1;
}

.range-inputs {
  display: flex;
  align-items: center;
  gap: 6px;
}

.range-inputs input {
  width: 96px;
}

.range-sep {
  color: #64748b;
  font-size: 0.82rem;
}

.filters-meta {
  margin-left: auto;
  align-items: flex-start;
}

.filters-count {
  color: #475569;
  font-size: 0.82rem;
  font-weight: 600;
}

.filters-meta .small-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.table-wrap {
  flex: 1 1 auto;
  min-height: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: auto;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
}

.sort-btn {
  margin-left: 6px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 0.78rem;
  line-height: 1;
  padding: 0 2px;
  color: #94a3b8;
}

.sort-btn.is-active {
  color: #2563eb;
}

.periodic-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 1060px;
  font-size: 0.92rem;
}

.periodic-table thead th {
  position: sticky;
  top: 0;
  z-index: 3;
  text-align: left;
  background: #f8fafc;
  color: #334155;
  border-bottom: 1px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  padding: 10px;
  white-space: nowrap;
}

.periodic-table thead th:last-child {
  border-right: none;
}

.periodic-table tbody td {
  padding: 10px;
  border-bottom: 1px solid #f1f5f9;
  border-right: 1px solid #e2e8f0;
  white-space: nowrap;
}

.periodic-table tbody td:last-child {
  border-right: none;
}

.badge {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 999px;
}

.table-row.is-green {
  box-shadow: inset 4px 0 0 #16a34a;
}

.table-row.is-green .badge {
  background: #dcfce7;
  color: #166534;
}

.table-row.is-yellow {
  box-shadow: inset 4px 0 0 #f59e0b;
}

.table-row.is-yellow .badge {
  background: #fef3c7;
  color: #92400e;
}

.table-row.is-red {
  box-shadow: inset 4px 0 0 #dc2626;
}

.table-row.is-red .badge {
  background: #fee2e2;
  color: #991b1b;
}

.table-row.is-overdue {
  box-shadow: inset 4px 0 0 #ef4444;
  animation: overdue-blink 1s infinite;
}

.table-row.is-overdue .badge {
  background: #ef4444;
  color: #fff;
}

.table-row.is-info-missing {
  box-shadow: inset 4px 0 0 #64748b;
}

.table-row.is-info-missing .badge {
  background: #e2e8f0;
  color: #334155;
}

.error-box,
.empty-box {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 12px;
}

.error-box {
  color: #b91c1c;
  border-color: #fecaca;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.modal-card {
  width: min(720px, 100%);
  max-height: 80vh;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-head {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-head h3 {
  margin: 0;
}

.close-btn,
.small-btn {
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
}

.modal-actions {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
}

.footer-actions {
  justify-content: flex-end;
}

.small-btn.primary {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
}

.units-list {
  overflow: auto;
  padding: 12px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px;
}

.unit-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.unit-km-input {
  margin-left: auto;
  width: 120px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 4px 8px;
}

.schedule-modal {
  width: min(560px, 100%);
}

.modal-body {
  padding: 12px;
  display: grid;
  gap: 10px;
}

.form-row {
  display: grid;
  gap: 6px;
}

.form-row label {
  color: #334155;
  font-size: 0.88rem;
  font-weight: 600;
}

.form-row input,
.form-row textarea {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.9rem;
}

.form-row textarea {
  min-height: 90px;
  resize: vertical;
}

@keyframes overdue-blink {
  0% {
    box-shadow: 0 0 0 rgba(239, 68, 68, 0.1);
    background: #fff;
  }
  50% {
    box-shadow: 0 0 14px rgba(239, 68, 68, 0.6);
    background: #fff5f5;
  }
  100% {
    box-shadow: 0 0 0 rgba(239, 68, 68, 0.1);
    background: #fff;
  }
}
</style>
